import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppToastComponent, ToastData, ToastType } from 'src/app/shared/ui/app-toast/app-toast.component';

/**
 * App-wide toasts: top centre, one at a time (a new one replaces the last),
 * shown with AppToastComponent (icon, title, message, close, countdown bar).
 *
 * The second argument callers pass ('Success', 'Failed', 'Warning', ...) is used
 * as the toast's title.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  verticalPosition: 'top' | 'bottom' = 'top';
  horizontalPosition: 'start' | 'center' | 'end' | 'left' | 'right' = 'center';
  duration: number = 3500; // ms
  errorDuration: number = 5000; // errors stay a little longer

  // words some callers pass as the second argument that aren't real titles
  private readonly notTitles = ['cancel', 'close', 'ok', 'okay', 'dismiss'];

  constructor(private _snackBar: MatSnackBar) {}

  success(message: string, title?: string) {
    this.show('success', message, title);
  }

  error(message: string, title?: string) {
    this.show('error', message, title, this.errorDuration);
  }

  warning(message: string, title?: string) {
    this.show('warning', message, title);
  }

  /** Neutral notice — for telling the user what is happening rather than that
   *  something went right or wrong (e.g. a feature still being built). */
  info(message: string, title?: string) {
    this.show('info', message, title);
  }

  private show(type: ToastType, message: string, title?: string, duration = this.duration) {
    const cleanTitle = title && !this.notTitles.includes(title.trim().toLowerCase()) ? title : undefined;
    const data: ToastData = { type, title: cleanTitle, message: message ?? '', duration };
    this._snackBar.openFromComponent(AppToastComponent, {
      data,
      duration,
      verticalPosition: this.verticalPosition,
      horizontalPosition: this.horizontalPosition,
      panelClass: ['app-toast-panel'],
    });
  }
}
